import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Badge, ButtonGroup } from 'react-bootstrap';

/**
 * FieldConfigurationTable - Presentational component for displaying field configuration data
 * 
 * @param {Array} fields - Array of field objects
 * @param {boolean} loading - Loading state indicator
 * @param {Function} onCreate - Callback when add button is clicked
 * @param {Function} onEdit - Callback when edit button is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {Function} onImport - Callback when import button is clicked
 * @param {Function} onExport - Callback when export button is clicked
 * @param {Function} onCopy - Callback when copy button is clicked
 */
const FieldConfigurationTable = ({ 
  fields, 
  loading, 
  onCreate, 
  onEdit, 
  onDelete, 
  onImport, 
  onExport, 
  onCopy 
}) => {
  const getFieldTypeLabel = (fieldType) => {
    return fieldType === 0 ? 'Header' : 'Detail';
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Field Configuration</h5>
        <ButtonGroup>
          <Button variant="primary" size="sm" onClick={onCreate}>
            Add Field
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onImport}>
            Import
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onExport}>
            Export
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onCopy}>
            Copy from Rule Set
          </Button>
        </ButtonGroup>
      </div>

      {!fields || fields.length === 0 ? (
        <div className="text-center p-4 text-muted">
          No fields configured. Click "Add Field" to create one.
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Display Name</th>
              <th>Field Key</th>
              <th>Type</th>
              <th>Data Type</th>
              <th>Required</th>
              <th>Description</th>
              <th style={{ width: '80px' }}>Decimal Places</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(field => (
              <tr key={field.id}>
                <td>{field.name}</td>
                <td>{field.displayName}</td>
                <td><code>{field.fieldKey}</code></td>
                <td>
                  <Badge bg={field.fieldType === 0 ? 'primary' : 'info'}>
                    {getFieldTypeLabel(field.fieldType)}
                  </Badge>
                </td>
                <td>{field.dataType}</td>
                <td>
                  <Badge bg={field.isRequired ? 'warning' : 'secondary'}>
                    {field.isRequired ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td className="text-truncate" style={{ maxWidth: '200px' }}>
                  {field.description || '-'}
                </td>
                <td className="text-center">{field.decimalPrecision != null ? field.decimalPrecision : ''}</td>
                <td>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-2"
                    onClick={() => onEdit(field)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => onDelete(field.id)}
                    disabled={field.isSystemDefault}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

FieldConfigurationTable.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      fieldKey: PropTypes.string.isRequired,
      fieldType: PropTypes.number.isRequired,
      dataType: PropTypes.string.isRequired,
      isRequired: PropTypes.bool,
      description: PropTypes.string,
      isSystemDefault: PropTypes.bool,
      decimalPrecision: PropTypes.number
    })
  ).isRequired,
  loading: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired
};

FieldConfigurationTable.defaultProps = {
  loading: false
};

export default FieldConfigurationTable;
